import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  IconButton,
  MenuItem,
  Tooltip,
  Typography,
  Grid,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  FormControl,
  FormControlLabel,
  Card,
  Box,
} from "@mui/material";
import {
  StyledButton,
  StyledInputLabel,
  StyledSelect,
  StyledTextField,
  StyledTableCellHistory,
  StyledTableRowHistory,
  StyledDatePicker,
  OutlinedIconFab,
  StyledMultilineTextField,
  StyledTableContainer,
  StyledCheckbox,
} from "../StyledComponents";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import MicOffIcon from "@mui/icons-material/MicOff";
import ClearIcon from "@mui/icons-material/Clear";
import { durationType } from "../initialData/initialData";
import {
  getHumanReadableDiffInPastDateWithAgo,
  getHumanReadableDurationInDate,
} from "../Utils/UtilsFunctions";
import Icon from "../Utils/Icons";
import DurationAutocompleteField from "./DurationAutocompleteField";
import {
  durationUnitMap,
  normalizeLegacyDuration,
  parseCombinedDuration,
  formatDurationTextForDisplay,
} from "./durationUtils";
import useSectionFieldLayout from "../config/hooks/useSectionFieldLayout";
import { FIELD_SET_IDS } from "../config/defaults/fieldLayoutUtils";

const MedicalHistory = ({
  medicalHistoryData,
  setMedicalHistoryData,
  dateForAppointment,
  activeHistorySection,
  setActiveHistorySection,
  startRecording,
  stopRecording,
  isAiEnabled,
  sectionName = "History of Past Illness",
  fieldLayout: fieldLayoutProp,
}) => {
  // Both Illness sub-sections share this component, so the fallback layout used
  // before the per-doctor config resolves must match the section being rendered.
  const fieldLayout = useSectionFieldLayout(
    fieldLayoutProp,
    sectionName === "History of Present Illness"
      ? FIELD_SET_IDS.medicalHistoryPresent
      : FIELD_SET_IDS.medicalHistory,
  );
  // Per-section default for Clinical Status: driven by each fieldSet's config
  // (Past Illness defaults to "Inactive"; Present Illness has no default → "").
  // When the field is toggled off in the Doctor Console Manager it is dropped
  // from the layout, so no default is applied and nothing is sent for it.
  const clinicalStatusField = fieldLayout.getField("clinical_status_description");
  const clinicalStatusEnabled = Boolean(clinicalStatusField);
  const defaultClinicalStatus = clinicalStatusEnabled
    ? clinicalStatusField?.defaultValue ?? ""
    : "";
  // Only Past Illness lets the doctor pick which old records go into the prescription.
  const showPrescriptionInclusion = sectionName === "History of Past Illness";
  console.log("History of Past Illness called", medicalHistoryData);
  const today = dayjs(dateForAppointment);
  const [chiefComplaintsStatus, setChiefComplaintsStatus] = useState([
    "Active",
    "Recurrence",
    "Relapse",
    "Inactive",
    "Remission",
    "Resolved",
    "Unknown",
  ]);

  const [authToken, setAuthToken] = useState(
    sessionStorage.getItem("accessToken") !== undefined &&
      sessionStorage.getItem("accessToken") !== null
      ? sessionStorage.getItem("accessToken")
      : ""
  );
  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const headers = {
    Authorization: `Bearer ${authToken}`,
  };
  const [autocompleteData, setAutoCompleteData] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [suggestApiCalled, setSuggestApiCalled] = useState(false);
  const [debounceFunctionCalledCondition, setDebounceFunctionCalledCondition] =
    useState(false);
  const { transcript, resetTranscript } = useSpeechRecognition();
  const [listeningOn, setListeningOn] = useState(false);
  const [medicalHistoryVars, setMedicalHistoryVars] = useState({
    condition: "",
    id: null,
    onset_datetime: null,
    clinical_status_description: defaultClinicalStatus,
    abatement_datetime: null,
    clinical_note: "",
    duration: "",
    abatement_duration: "",
  });
  const [termSelectedOrAdded, setTermSelectedOrAdded] = useState(false);
  const [conditionName, setConditionName] = useState("");

  const [clinicalNotesListening, setClinicalNotesListening] = useState(false);
  const [error, setError] = useState("");
  const [medicalHistoryEditIndex, setMedicalHistoryEditIndex] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const hasPendingDurationSelection = (durationValue) =>
    /^\d+$/.test(String(durationValue || "").trim());
  const [mouseDownOnOption, setMouseDownOnOption] = useState(false);

  const debounceTimeout = useRef(null); // Use useRef to persist timeout
  const skipEffect = useRef(false);
  const optionChosen = useRef(false);
  console.log("medicalHistoryVars", medicalHistoryVars);

  useEffect(() => {
    setAuthToken(sessionStorage.getItem("accessToken"));
  }, [sessionStorage.getItem("accessToken")]);

  // The field layout resolves asynchronously (remote per-doctor config), so the
  // initial state can hold the built-in default before the real config lands.
  // Re-apply the resolved default once it changes, unless a row is being edited.
  useEffect(() => {
    if (medicalHistoryEditIndex !== null) return;
    setMedicalHistoryVars((prev) =>
      prev.clinical_status_description === defaultClinicalStatus
        ? prev
        : { ...prev, clinical_status_description: defaultClinicalStatus }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultClinicalStatus]);

  useEffect(() => {
    if (activeHistorySection == sectionName) {
      let timerId;
      // alert(`MH ${transcript}`);
      timerId = setTimeout(() => {
        if (clinicalNotesListening) {
          setMedicalHistoryVars({
            ...medicalHistoryVars,
            clinical_note: transcript, // Use computed property name to set the field dynamically
          });
        } else {
          setConditionName(transcript);
        }
      }, 3000);

      return () => {
        clearTimeout(timerId);
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript]);
  useEffect(() => {
    if (skipEffect.current) {
      skipEffect.current = false; // reset after skipping
      return;
    }
    // Call the API with the debounced function when conditionName changes

    let timerOut = setTimeout(() => {
      if (conditionName.trim() !== "") {
        debouncedConditionFilterCall(conditionName.toLowerCase());
      }
    }, 500);
    return () => clearInterval(timerOut);
  }, [conditionName]);

  const startListening = (key) => {
    // alert("MH SL");
    if (transcript) {
      resetTranscript();
    }
    setActiveHistorySection(sectionName);
    SpeechRecognition.startListening({ continuous: true, language: "en-IN" });
    setListeningOn(true);
    if (key == "condition") {
      setClinicalNotesListening(false);
    } else {
      setClinicalNotesListening(true);
    }
  };
  const validateFields = () => {
    const errors = {};

    if (
      fieldLayout.isFieldVisible("clinical_note", medicalHistoryVars) &&
      fieldLayout.isFieldRequired("clinical_note", medicalHistoryVars) &&
      !medicalHistoryVars.clinical_note?.trim()
    ) {
      errors.clinical_note = fieldLayout.getValidationMessage(
        "clinical_note",
        "Clinical Notes is a mandatory field.",
      );
    }

    if (
      fieldLayout.isFieldVisible("duration", medicalHistoryVars) &&
      fieldLayout.isFieldRequired("duration", medicalHistoryVars) &&
      !medicalHistoryVars.duration
    ) {
      errors.duration = fieldLayout.getValidationMessage(
        "duration",
        "Duration is a mandatory field.",
      );
    } else if (
      fieldLayout.isFieldVisible("duration", medicalHistoryVars) &&
      hasPendingDurationSelection(medicalHistoryVars.duration)
    ) {
      errors.duration = "Please select a duration option.";
    }

    if (
      fieldLayout.isFieldVisible("abatement_duration", medicalHistoryVars) &&
      fieldLayout.isFieldRequired("abatement_duration", medicalHistoryVars) &&
      !medicalHistoryVars.abatement_duration
    ) {
      errors.abatement_duration = fieldLayout.getValidationMessage(
        "abatement_duration",
        "Condition Duration is a mandatory field.",
      );
    } else if (
      fieldLayout.isFieldVisible("abatement_duration", medicalHistoryVars) &&
      hasPendingDurationSelection(medicalHistoryVars.abatement_duration)
    ) {
      errors.abatement_duration = "Please select a duration option.";
    }

    if (
      fieldLayout.isFieldVisible("clinical_status_description", medicalHistoryVars) &&
      fieldLayout.isFieldRequired("clinical_status_description", medicalHistoryVars) &&
      !medicalHistoryVars.clinical_status_description
    ) {
      errors.clinical_status_description = fieldLayout.getValidationMessage(
        "clinical_status_description",
        "Clinical Status is a mandatory field.",
      );
    }

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  };
  const handleAddRow = async () => {
    if (!validateFields()) {
      return;
    }
    // console.log("Add Click", medicalHistoryVars);
    if (medicalHistoryVars.condition || conditionName.length > 0) {
      // Store the updated state in a local variable

      let newDetails,
        updatedFound = {},
        updatedSelectedOptiondetsils = {},
        foundData = false;

      if (!optionChosen.current) {
        const postBody = {
          term: conditionName,
          state: "active",
          semantictag: "finding++disorder",
          acceptability: "synonyms",
          returnlimit: "100",
          refsetid: null,
          parentid: null,
          excludeparentconcept: false,
          fullconcept: true,
        };

        try {
          if (conditionName.length > 1) {
            const response = await axios.post(
              `${REACT_APP_BACKEND_ROUTE_URL}snomed/search/`,
              postBody,
              { headers: headers }
            );
            if (response) {
              console.log("Snomed Response", response);
              if (
                response.data.status === "Success" &&
                response.data.data.length > 0
              ) {
                foundData = response.data.data.find(
                  (d) => d.term === conditionName
                );
                console.log("Found Data", foundData);
                if (foundData) {
                  updatedFound = {
                    ...foundData,
                    found: true,
                    id: foundData.conceptId,
                  };
                } else {
                  updatedSelectedOptiondetsils = {
                    hierarchy: null,
                    isPreferredTerm: null,
                    conceptState: null,
                    conceptFsn: conditionName,
                    definitionStatus: null,
                    conceptId: null,
                    languageCode: "en",
                    typeId: null,
                    term: conditionName,
                    caseSignificanceId: "CASE_INSENSITIVE",
                    id: null,
                    effectiveTime: null,
                    activeStatus: null,
                    moduleId: null,
                  };
                }
              } else {
                updatedSelectedOptiondetsils = {
                  hierarchy: null,
                  isPreferredTerm: null,
                  conceptState: null,
                  conceptFsn: conditionName,
                  definitionStatus: null,
                  conceptId: null,
                  languageCode: "en",
                  typeId: null,
                  term: conditionName,
                  caseSignificanceId: "CASE_INSENSITIVE",
                  id: null,
                  effectiveTime: null,
                  activeStatus: null,
                  moduleId: null,
                };
              }

              if (foundData) {
                console.log("updatedFound", updatedFound);
                newDetails = {
                  ...medicalHistoryVars,
                  condition: updatedFound.term,
                  id: updatedFound.id,
                };
              } else {
                console.log(
                  "updatedSelectedOptiondetsils",
                  updatedSelectedOptiondetsils
                );
                newDetails = {
                  ...medicalHistoryVars,
                  condition: updatedSelectedOptiondetsils.term,
                  id: updatedSelectedOptiondetsils.id,
                };
              }
            } else {
              newDetails = {
                ...medicalHistoryVars,
                condition: conditionName,
                id: null,
              };
            }
          }
        } catch (err) {
          console.log(err);
          // toast.error(err);
          newDetails = {
            ...medicalHistoryVars,
            condition: conditionName,
            id: null,
          };
        }
      } else {
        newDetails = { ...medicalHistoryVars };
      }

      const newRow = {
        condition: newDetails.condition,
        id: newDetails.id,
        onset_datetime: newDetails?.onset_datetime?.format("YYYY-MM-DD"),
        clinical_status_description: clinicalStatusEnabled
          ? newDetails.clinical_status_description
          : "",
        abatement_datetime: newDetails?.abatement_datetime
          ? newDetails?.abatement_datetime.format("YYYY-MM-DD")
          : null,
        clinical_note: newDetails?.clinical_note,
        duration: normalizeLegacyDuration(
          newDetails?.duration,
          newDetails?.duration,
          newDetails?.chiefComplaintDurationType
        ),
        abatement_duration: normalizeLegacyDuration(
          newDetails?.abatement_duration,
          newDetails?.abatement_duration,
          newDetails?.abatement_duration_type
        ),
      };

      // Update family history rows by adding the new row

      const updatedRows = [...medicalHistoryData["new"], newRow];

      setMedicalHistoryData({
        ...medicalHistoryData,
        new: updatedRows,
      });
      // Reset family history variables and clear error
      setMedicalHistoryVars({
        ...medicalHistoryVars,
        condition: "",
        id: null,
        clinical_note: "",
        duration: "",
        onset_datetime: null,
        abatement_duration: "",
        abatement_datetime: null,
        clinical_status_description: defaultClinicalStatus,
      });
      setConditionName("");
      setError("");
      setValidationErrors({});
      setDebounceFunctionCalledCondition(false);
      setTermSelectedOrAdded(false);
      optionChosen.current = false;
      setFilteredOptions([]);
    } else {
      // Set error message if any field is missing
      setError("Condition Name field is mandatory");
    }
  };
  const handleMedicalHistoryEditClick = (row, rowIndex) => {
    setConditionName(row.condition);
    setMedicalHistoryVars({
      condition: "",
      id: null,
      onset_datetime: row.onset_datetime ? dayjs(row.onset_datetime) : null,
      abatement_datetime: row?.abatement_datetime
        ? dayjs(row?.abatement_datetime)
        : null,
      clinical_status_description: row?.clinical_status_description,
      clinical_note: row?.clinical_note,
      duration: normalizeLegacyDuration(
        row?.duration,
        row?.duration,
        row?.chiefComplaintDurationType
      ),
      abatement_duration: normalizeLegacyDuration(
        row?.abatement_duration,
        row?.abatement_duration,
        row?.abatement_duration_type
      ),
    });
    setMedicalHistoryEditIndex(rowIndex);
  };
  const handleMedicalHistoryUpdate = async () => {
    if (!validateFields()) {
      return;
    }
    // console.log("Update Click", medicalHistoryVars);
    if (medicalHistoryVars.condition || conditionName.length > 0) {
      // Store the updated state in a local variable

      let newDetails,
        updatedFound = {},
        updatedSelectedOptiondetsils = {},
        foundData = false;

      if (!optionChosen.current) {
        const postBody = {
          term: conditionName,
          state: "active",
          semantictag: "finding++disorder",
          acceptability: "synonyms",
          returnlimit: "100",
          refsetid: null,
          parentid: null,
          excludeparentconcept: false,
          fullconcept: true,
        };

        try {
          if (conditionName.length > 1) {
            const response = await axios.post(
              `${REACT_APP_BACKEND_ROUTE_URL}snomed/search/`,
              postBody,
              { headers: headers }
            );
            if (response) {
              console.log("Snomed Response", response);
              if (
                response.data.status === "Success" &&
                response.data.data.length > 0
              ) {
                foundData = response.data.data.find(
                  (d) => d.term === conditionName
                );
                console.log("Found Data", foundData);
                if (foundData) {
                  updatedFound = {
                    ...foundData,
                    found: true,
                    id: foundData.conceptId,
                  };
                } else {
                  updatedSelectedOptiondetsils = {
                    hierarchy: null,
                    isPreferredTerm: null,
                    conceptState: null,
                    conceptFsn: conditionName,
                    definitionStatus: null,
                    conceptId: null,
                    languageCode: "en",
                    typeId: null,
                    term: conditionName,
                    caseSignificanceId: "CASE_INSENSITIVE",
                    id: null,
                    effectiveTime: null,
                    activeStatus: null,
                    moduleId: null,
                  };
                }
              } else {
                updatedSelectedOptiondetsils = {
                  hierarchy: null,
                  isPreferredTerm: null,
                  conceptState: null,
                  conceptFsn: conditionName,
                  definitionStatus: null,
                  conceptId: null,
                  languageCode: "en",
                  typeId: null,
                  term: conditionName,
                  caseSignificanceId: "CASE_INSENSITIVE",
                  id: null,
                  effectiveTime: null,
                  activeStatus: null,
                  moduleId: null,
                };
              }

              if (foundData) {
                console.log("updatedFound", updatedFound);
                newDetails = {
                  ...medicalHistoryVars,
                  condition: updatedFound.term,
                  id: updatedFound.id,
                };
              } else {
                console.log(
                  "updatedSelectedOptiondetsils",
                  updatedSelectedOptiondetsils
                );
                newDetails = {
                  ...medicalHistoryVars,
                  condition: updatedSelectedOptiondetsils.term,
                  id: updatedSelectedOptiondetsils.id,
                };
              }
            } else {
              newDetails = {
                ...medicalHistoryVars,
                condition: conditionName,
                id: null,
              };
            }
          }
        } catch (err) {
          console.log(err);
          // toast.error(err);
          newDetails = {
            ...medicalHistoryVars,
            condition: conditionName,
            id: null,
          };
        }
      } else {
        newDetails = { ...medicalHistoryVars };
      }

      const updatedRow = {
        condition: newDetails?.condition,
        id: newDetails?.id,
        onset_datetime: newDetails?.onset_datetime?.format("YYYY-MM-DD"),
        abatement_datetime:
          newDetails?.abatement_datetime?.format("YYYY-MM-DD"),
        clinical_status_description: clinicalStatusEnabled
          ? newDetails?.clinical_status_description
          : "",
        clinical_note: newDetails?.clinical_note,
        duration: normalizeLegacyDuration(
          newDetails?.duration,
          newDetails?.duration,
          newDetails?.chiefComplaintDurationType
        ),
        abatement_duration: normalizeLegacyDuration(
          newDetails?.abatement_duration,
          newDetails?.abatement_duration,
          newDetails?.abatement_duration_type
        ),
        source: undefined,
      };

      // Update family history rows by adding the new row

      const updatedMedicalHistory = medicalHistoryData["new"].map(
        (row, index) => {
          if (index === medicalHistoryEditIndex) {
            return updatedRow;
          } else {
            return row;
          }
        }
      );
      setMedicalHistoryData({
        ...medicalHistoryData,
        new: updatedMedicalHistory,
      });
      // Reset family history variables and clear error
      setMedicalHistoryVars({
        ...medicalHistoryVars,
        condition: "",
        id: null,
        clinical_note: "",
        duration: "",
        onset_datetime: null,
        abatement_duration: "",
        abatement_datetime: null,
        clinical_status_description: defaultClinicalStatus,
      });
      setConditionName("");

      setMedicalHistoryEditIndex(null);
      setError("");
      setValidationErrors({});
      setDebounceFunctionCalledCondition(false);
      setTermSelectedOrAdded(false);
      setFilteredOptions([]);
      optionChosen.current = false;
    } else {
      // Set error message if any field is missing
      setError("Condition Name field is mandatory");
    }
  };
  const handlePrescriptionInclusionToggle = (rowIndex) => {
    const updatedPrev = medicalHistoryData["prev"].map((row, index) =>
      index === rowIndex
        ? { ...row, include_in_prescription: !row.include_in_prescription }
        : row
    );
    setMedicalHistoryData({
      ...medicalHistoryData,
      prev: updatedPrev,
    });
  };
  const handleSelectAllPreviousPrescription = (checked) => {
    const updatedPrev = medicalHistoryData["prev"].map((row) => ({
      ...row,
      include_in_prescription: checked,
    }));
    setMedicalHistoryData({
      ...medicalHistoryData,
      prev: updatedPrev,
    });
  };
  const allPreviousSelectedForPrescription =
    showPrescriptionInclusion &&
    medicalHistoryData["prev"].length > 0 &&
    medicalHistoryData["prev"].every((row) => !!row.include_in_prescription);
  const handleMedicalHistoryDelete = (rowIndex) => {
    // Filter out the row at the specified index
    const updatedMedicalHistory = medicalHistoryData["new"].filter(
      (row, index) => index !== rowIndex
    );
    // Update the state with the filtered array
    setMedicalHistoryData({
      ...medicalHistoryData,
      new: updatedMedicalHistory,
    });
  };
  const calculateOnsetDate = (duration, type) => {
    if (!duration) return null;
    const today = dayjs(dateForAppointment); // appointment date
    return today.subtract(duration, durationUnitMap[type]);
  };

  const calculateAbatementDate = (onset_datetime, duration, type) => {
    if (
      duration === null ||
      duration === undefined ||
      isNaN(parseInt(duration)) ||
      !onset_datetime
    )
      return null;
    console.log(
      "date abatement",
      dayjs(onset_datetime).add(duration, durationUnitMap[type])
    );
    return dayjs(onset_datetime).add(duration, durationUnitMap[type]);
  };

  const handleChangeMedicalHistory = (field, value) => {
    let newDetails;
    if (field == "condition") {
      setAutoCompleteData([]);
      setFilteredOptions([]);
      setTermSelectedOrAdded(true);
      newDetails = {
        ...medicalHistoryVars,
        [field]: value.term,
        id: value.id,
      };
    } else {
      newDetails = {
        ...medicalHistoryVars,
        [field]: value,
      };
    }

    if (field === "duration") {
      const { value: durationValue, unit: durationUnit } = parseCombinedDuration(
        newDetails.duration
      );
      newDetails = {
        ...newDetails,
        onset_datetime:
          durationValue && durationUnit
            ? calculateOnsetDate(parseInt(durationValue, 10), durationUnit)
            : null,
      };
    }
    if (field === "abatement_duration") {
      const { value: durationValue, unit: durationUnit } = parseCombinedDuration(
        newDetails.abatement_duration
      );
      const newAbatementDate =
        durationValue && durationUnit
          ? calculateAbatementDate(
            newDetails?.onset_datetime,
            parseInt(durationValue, 10),
            durationUnit
          )
          : null;
      newDetails = {
        ...newDetails,
        abatement_datetime: newAbatementDate,
      };
    }

    setMedicalHistoryVars(newDetails);
  };
  const handleClearConditionName = () => {
    setError("");
    setConditionName("");
    resetTranscript();
    setMedicalHistoryVars({
      ...medicalHistoryVars,
      condition: "", // Use computed property name to set the field dynamically
    });
    setTermSelectedOrAdded(false);
    setFilteredOptions([]);
    setDebounceFunctionCalledCondition(false);
    // setMedicineList([])
  };
  const handleClearClinicalNotes = () => {
    setError("");

    resetTranscript();
    setMedicalHistoryVars({
      ...medicalHistoryVars,
      clinical_note: "", // Use computed property name to set the field dynamically
    });
    // setMedicineList([])
  };
  const debouncedConditionFilterCall = async (medicine) => {
    console.log("Debounce function called");
    let semanticTagForConditionSearch = "finding++disorder";
    const postBody = {
      term: medicine,
      state: "active",
      semantictag: semanticTagForConditionSearch,
      acceptability: "synonyms",
      returnlimit: "100",
      refsetid: null,
      parentid: null,
      excludeparentconcept: false,
      fullconcept: true,
    };

    try {
      if (conditionName.length > 1) {
        const response = await axios.post(
          `${REACT_APP_BACKEND_ROUTE_URL}snomed/search/`,
          postBody,
          { headers: headers }
        );
        if (response) {
          console.log("Snomed Response", response);
          if (
            response.data.status === "Success" &&
            response.data.data.length > 0
          ) {
            const dataToProcess =
              response.data.data.length > 300
                ? response.data.data.slice(0, 300)
                : response.data.data;

            const filteredData = dataToProcess.map((medicineItem) => {
              return {
                term: medicineItem.term,
                id: medicineItem.conceptId,
              };
            });
            console.log("Filtered Medicine Data", filteredData);
            setAutoCompleteData(filteredData);
            setFilteredOptions(filteredData);
          } else {
            try {
              if (conditionName.length > 1) {
                const response = await axios.post(
                  `${REACT_APP_BACKEND_ROUTE_URL}snomed/suggest/`,
                  postBody,
                  { headers: headers }
                );
                if (response) {
                  console.log("Suggest Response", response);
                  if (
                    response.data.status === "Success" &&
                    response.data.data.length > 0
                  ) {
                    setSuggestApiCalled(true);
                    const dataToProcess =
                      response.data.data.length > 300
                        ? response.data.data.slice(0, 300)
                        : response.data.data;
                    setAutoCompleteData(dataToProcess);
                    setFilteredOptions(dataToProcess);
                    // setSelectedFromSuggestion(true);
                  } else {
                    // setSelectedFromSuggestion(true);

                    setAutoCompleteData([]);
                    setFilteredOptions([]);
                  }
                }
              }
            } catch (err) {
              console.log(err);
              toast.error(err);
            }
          }
          setDebounceFunctionCalledCondition(true);
        }
      }
    } catch (err) {
      console.log(err);
      toast.error(err);
    }
  }; // Adjust the debounce duration (in milliseconds) as needed
  const handleAddOption = async (term) => {
    const postBody = {
      term: term,
      state: "active",
      semantictag: "finding++disorder",
      acceptability: "synonyms",
      returnlimit: "100",
      refsetid: null,
      parentid: null,
      excludeparentconcept: false,
      fullconcept: true,
    };

    try {
      if (conditionName.length > 1) {
        const response = await axios.post(
          `${REACT_APP_BACKEND_ROUTE_URL}snomed/search/`,
          postBody,
          { headers: headers }
        );
        if (response) {
          let updatedFound = {},
            updatedSelectedOptiondetsils = {},
            foundData = false;
          console.log("Snomed Response", response);
          if (
            response.data.status === "Success" &&
            response.data.data.length > 0
          ) {
            foundData = response.data.data.find((d) => d.term === term);
            console.log("Found Data", foundData);
            if (foundData) {
              updatedFound = {
                ...foundData,
                found: true,
                id: foundData.conceptId,
              };
            } else {
              updatedSelectedOptiondetsils = {
                hierarchy: null,
                isPreferredTerm: null,
                conceptState: null,
                conceptFsn: term,
                definitionStatus: null,
                conceptId: null,
                languageCode: "en",
                typeId: null,
                term: term,
                caseSignificanceId: "CASE_INSENSITIVE",
                id: null,
                effectiveTime: null,
                activeStatus: null,
                moduleId: null,
              };
            }
          } else {
            updatedSelectedOptiondetsils = {
              hierarchy: null,
              isPreferredTerm: null,
              conceptState: null,
              conceptFsn: term,
              definitionStatus: null,
              conceptId: null,
              languageCode: "en",
              typeId: null,
              term: term,
              caseSignificanceId: "CASE_INSENSITIVE",
              id: null,
              effectiveTime: null,
              activeStatus: null,
              moduleId: null,
            };
          }

          setAutoCompleteData([]);
          setFilteredOptions([]);
          setSuggestApiCalled(false);
          setDebounceFunctionCalledCondition(true);
          if (foundData) {
            handleChangeMedicalHistory("condition", updatedFound);
          } else {
            handleChangeMedicalHistory(
              "condition",
              updatedSelectedOptiondetsils
            );
          }
        }
      }
    } catch (err) {
      console.log(err);
      toast.error(err);
    }
  };

  const handleConditionTyped = (value) => {
    setConditionName(value);

    // if (debounceTimeout.current) {
    //   clearTimeout(debounceTimeout.current); // Clear previous timeout
    // }

    // debounceTimeout.current = setTimeout(() => {
    //   handleAddOption(value); // Only triggers after user stops typing
    // }, 500); // Adjust delay as needed
  };

  const renderMedicalTableCell = (column, row, { isPrev = false, isNew = false } = {}) => {
    switch (column.key) {
      case "condition":
        return (
          <>
            {isNew ? row?.condition : row.condition}
            {isNew && row?.source === "voice" && !row.id && (
              <Tooltip title="Machine-interpreted text — please verify before confirming">
                <span className="missing-id-warning">⚠️</span>
              </Tooltip>
            )}
          </>
        );
      case "clinical_note":
        return isNew
          ? row?.clinical_note?.length > 0
            ? row.clinical_note
            : "-"
          : row?.clinical_note?.length > 0
            ? row.clinical_note
            : "-";
      case "clinical_status_description":
        return row?.clinical_status_description ?? "-";
      case "onset_datetime":
        if (isPrev) {
          return row?.onset_datetime
            ? formatDurationTextForDisplay(
                getHumanReadableDiffInPastDateWithAgo(row?.onset_datetime, row),
              )
            : "-";
        }
        return normalizeLegacyDuration(
          row?.duration,
          row?.duration,
          row?.chiefComplaintDurationType,
        )
          ? formatDurationTextForDisplay(
              `${normalizeLegacyDuration(
                row?.duration,
                row?.duration,
                row?.chiefComplaintDurationType,
              )} ago`,
            )
          : "-";
      case "abatement_datetime":
        if (isPrev) {
          return row?.abatement_datetime
            ? formatDurationTextForDisplay(
                getHumanReadableDurationInDate(
                  row?.onset_datetime,
                  row?.abatement_datetime,
                  row,
                ),
              )
            : "-";
        }
        return (
          formatDurationTextForDisplay(
            normalizeLegacyDuration(
              row?.abatement_duration,
              row?.abatement_duration,
              row?.abatement_duration_type,
            ),
          ) || "-"
        );
      default:
        return row?.[column.key] ?? "-";
    }
  };

  return (
    <Card
      sx={{
        borderRadius: "10px",
        boxShadow: "0 4px 4px 0 #C7C7CC inset",
        backgroundColor: "#FFF",
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{ margin: "5px 0px", display: "flex", alignItems: "center" }}
      >
        {fieldLayout.isFieldVisible("condition", medicalHistoryVars) && (
        <Grid item {...fieldLayout.getFieldGrid("condition", { xs: 11, sm: 11, md: 11 })} sx={{ margin: "5px 0px" }}>
          <StyledTextField
            label={fieldLayout.getFieldLabel("condition", "Condition Name", medicalHistoryVars)}
            variant="outlined"
            value={
              // medicalHistoryVars.condition !== ""
              //   ? medicalHistoryVars.condition
              //   :
              conditionName
            }
            onChange={(e) => {
              // if (!medicalHistoryVars.condition) {
              //   setConditionName(e.target.value);
              // }
              handleConditionTyped(e.target.value);
            }}
            fullWidth
            onBlur={() => {
              setTimeout(() => {
                const shouldClear = !mouseDownOnOption;

                if (shouldClear) {
                  // setIsFocused(false);
                  setFilteredOptions([]);
                  setDebounceFunctionCalledCondition(false);
                  setTermSelectedOrAdded(false);
                }

                setMouseDownOnOption(false);
              }, 100);
            }}
            onFocus={() => {
              // setIsFocused(true);
              if (conditionName.trim() !== "") {
                debouncedConditionFilterCall(conditionName.toLowerCase()); // re-trigger filtering
              }
            }}
            InputProps={{
              endAdornment: (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <div style={{ marginRight: "5px" }}>
                    {fieldLayout.isCapabilityEnabled("condition", "clear") && (
                    <Tooltip title="Clear">
                      <OutlinedIconFab
                        size="small"
                        variant="contained"
                        onClick={handleClearConditionName}
                      >
                        <Icon name="clearTextField" size={32} />
                      </OutlinedIconFab>
                    </Tooltip>
                    )}
                  </div>
                  {/* {!termSelectedOrAdded && (
                    <Tooltip title="Add" style={{ marginRight: "5px" }}>
                      <OutlinedIconFab
                        size="small"
                        variant="contained"
                        onClick={handleAddOption}
                      >
                        <AddIcon />
                      </OutlinedIconFab>
                    </Tooltip>
                  )} */}
                  <>
                    {isAiEnabled &&
                      fieldLayout.isCapabilityEnabled("condition", "voice") &&
                      (listeningOn === false ? (
                        <Tooltip title="Start Listening">
                          <OutlinedIconFab
                            // onClick={() => startListening("condition")}
                            onClick={() => {
                              startRecording();
                              setListeningOn(true);
                            }}
                            variant="contained"
                            color="defaultColor"
                            size="small"
                          >
                            <Icon name="micNotInUse" size={32} />
                          </OutlinedIconFab>
                        </Tooltip>
                      ) : listeningOn === true &&
                        clinicalNotesListening === true ? (
                        <Tooltip title="Start Listening">
                          <OutlinedIconFab
                            // onClick={() => startListening("condition")}
                            onClick={() => {
                              startRecording();
                              setListeningOn(true);
                            }}
                            variant="contained"
                            color="default"
                            size="small"
                          >
                            <Icon name="micNotInUse" size={32} />
                          </OutlinedIconFab>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Stop Listening">
                          <OutlinedIconFab
                            onClick={() => {
                              // SpeechRecognition.stopListening();
                              stopRecording(sectionName);
                              setListeningOn(false);
                            }}
                            variant="outlined"
                            color="default"
                            size="small"
                          >
                            <Icon name="micInUse" size={32} />
                          </OutlinedIconFab>
                        </Tooltip>
                      ))}
                  </>
                </div>
              ),
            }}
          />
          {filteredOptions.length > 0 ? (
            <div
              style={{
                backgroundColor: "#f5f5f5",
                borderRadius: "10px",
                padding: "20px",
                maxHeight: "200px", // Set your desired maximum height here
                overflowY: "auto", // Add vertical scrolling
                // position: "absolute", // Position it absolutely
                // zIndex: 1000, // Set a high z-index value to bring it over other elements
              }}
            >
              {/* Display filtered medicines */}
              {suggestApiCalled && (
                <div style={{ color: "red" }}>
                  No Exact Matches found ! You can search for the following
                  Suggestions.
                </div>
              )}

              <>
                {filteredOptions.map((op) => (
                  <div key={op}>
                    {
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {op.added ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <StyledButton
                              color="success"
                              sx={{
                                textAlign: "left",
                                justifyContent: "flex-start",
                                width: "100%",
                              }}
                            >
                              {op.term}
                            </StyledButton>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                flexDirection: "row",
                                color: "green",
                                fontStyle: "bold",
                              }}
                            >
                              <span>&#10003;</span>
                              <span>(Added)</span>
                            </div>
                          </div>
                        ) : (
                          <StyledButton
                            color="grey"
                            sx={{
                              color: "#000",
                              textAlign: "left",
                              justifyContent: "flex-start",
                              width: "100%",
                            }}
                            onClick={() => {
                              skipEffect.current = true; // prevent debounce call
                              optionChosen.current = true;
                              setConditionName(op.term);
                              handleChangeMedicalHistory("condition", op);
                            }}
                            onMouseDown={() => setMouseDownOnOption(true)}
                          >
                            {op.term}
                          </StyledButton>
                        )}
                      </div>
                    }
                  </div>
                ))}
              </>
            </div>
          ) : filteredOptions.length == 0 &&
            debounceFunctionCalledCondition &&
            !termSelectedOrAdded ? (
            <div
              style={{
                backgroundColor: "#f5f5f5",
                borderRadius: "10px",
                padding: "20px",
                maxHeight: "300px", // Set your desired maximum height here
                overflowY: "auto", // Add vertical scrolling
              }}
            >
              <div style={{ color: "red" }}>No Options Found!</div>
            </div>
          ) : null}
        </Grid>
        )}
        {fieldLayout.isFieldVisible("clinical_note", medicalHistoryVars) && (
        <Grid item {...fieldLayout.getFieldGrid("clinical_note", { xs: 11, md: 11, sm: 11 })}>
          <StyledTextField
            variant="outlined"
            label={fieldLayout.getFieldLabel("clinical_note", "Clinical Notes", medicalHistoryVars)}
            // multiline
            // rows={2}
            value={medicalHistoryVars.clinical_note}
            onChange={(e) =>
              handleChangeMedicalHistory("clinical_note", e.target.value)
            }
            error={!!validationErrors.clinical_note}
            helperText={validationErrors.clinical_note}
            fullWidth
            InputProps={{
              endAdornment: (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <div style={{ marginRight: "5px" }}>
                    <Tooltip title="Clear">
                      <OutlinedIconFab
                        size="small"
                        variant="contained"
                        onClick={handleClearClinicalNotes}
                      >
                        <Icon name="clearTextField" size={32} />
                      </OutlinedIconFab>
                    </Tooltip>
                  </div>

                  <>
                    {/* {listeningOn === false ? (
                      <Tooltip title="Start Listening">
                        <OutlinedIconFab
                          onClick={() => startListening("clinical_note")}
                          variant="contained"
                          color="defaultColor"
                          size="small"
                        >
                          <MicOffIcon />
                        </OutlinedIconFab>
                      </Tooltip>
                    ) : listeningOn === true &&
                      clinicalNotesListening === false ? (
                      <Tooltip title="Start Listening">
                        <OutlinedIconFab
                          onClick={() => startListening("clinical_note")}
                          variant="contained"
                          color="default"
                          size="small"
                        >
                          <MicOffIcon />
                        </OutlinedIconFab>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Stop Listening">
                        <OutlinedIconFab
                          onClick={() => {
                            SpeechRecognition.stopListening();
                            setListeningOn(false);
                            setClinicalNotesListening(false);
                          }}
                          variant="outlined"
                          color="default"
                          size="small"
                        >
                          <KeyboardVoiceIcon />
                        </OutlinedIconFab>
                      </Tooltip>
                    )} */}
                  </>
                </div>
              ),
            }}
          />
        </Grid>
        )}

        {fieldLayout.isFieldVisible("duration", medicalHistoryVars) && (
        <Grid
          item
          {...fieldLayout.getFieldGrid("duration", { xs: 11, sm: 11, md: 5 })}
          sx={{ display: "flex", alignItems: "center", gap: 1, pr: { md: 1 } }}
        >
          <Tooltip title="How long ago the condition started">
            <Typography
              component="span"
              variant="body2"
              sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
            >
              {fieldLayout.getFieldLabel("duration", "Condition Onset:", medicalHistoryVars)}
            </Typography>
          </Tooltip>
          <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 200, md: 220 } }}>
            <DurationAutocompleteField
              label="Duration"
              value={medicalHistoryVars.duration || ""}
              onChange={(combined) =>
                handleChangeMedicalHistory("duration", combined)
              }
              durationType={durationType}
              error={!!validationErrors.duration}
              helperText={validationErrors.duration}
            />
          </Box>
        </Grid>
        )}

        {fieldLayout.isFieldVisible("abatement_duration", medicalHistoryVars) && (
        <Grid
          item
          {...fieldLayout.getFieldGrid("abatement_duration", { xs: 11, sm: 11, md: 6 })}
          sx={{ display: "flex", alignItems: "center", gap: 1, pl: { md: 1 } }}
        >
          <Tooltip title="How long the condition has been present">
            <Typography
              component="span"
              variant="body2"
              sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
            >
              {fieldLayout.getFieldLabel("abatement_duration", "Condition Duration:", medicalHistoryVars)}
            </Typography>
          </Tooltip>
          <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 200, md: 220 } }}>
            <DurationAutocompleteField
              label="Duration"
              value={medicalHistoryVars.abatement_duration || ""}
              onChange={(combined) =>
                handleChangeMedicalHistory("abatement_duration", combined)
              }
              durationType={durationType}
              error={!!validationErrors.abatement_duration}
              helperText={validationErrors.abatement_duration}
            />
          </Box>
        </Grid>
        )}

        {fieldLayout.isFieldVisible("clinical_status_description", medicalHistoryVars) && (
        <Grid item {...fieldLayout.getFieldGrid("clinical_status_description", { xs: 11, sm: 11, md: 9 })}>
          <StyledTextField
            select
            value={medicalHistoryVars.clinical_status_description}
            onChange={(e) =>
              handleChangeMedicalHistory(
                "clinical_status_description",
                e.target.value
              )
            }
            placeholder="Select Clinical Status"
            fullWidth
            label={fieldLayout.getFieldLabel("clinical_status_description", "Clinical Status", medicalHistoryVars)}
            error={!!validationErrors.clinical_status_description}
            helperText={validationErrors.clinical_status_description}
          >
            {chiefComplaintsStatus.map((status) => (
              <MenuItem key={status} value={status} sx={{ fontSize: "12px" }}>
                {status}
              </MenuItem>
            ))}
          </StyledTextField>
        </Grid>
        )}

        <Grid
          item
          xs={11}
          sm={11}
          md={2}
          sx={{
            margin: "5px 0px",
            display: "flex",
            justifyContent: { xs: "stretch", md: "flex-end" },
            padding: "0px",
          }}
        >
          <StyledButton
            bgColor="#0E3F66"
            onClick={
              medicalHistoryEditIndex !== null
                ? handleMedicalHistoryUpdate
                : handleAddRow
            }
            variant="contained"
            sx={{ width: { xs: "100%" }, marginLeft: { md: "auto" } }}
            color="defaultColor"
          >
            {medicalHistoryEditIndex !== null ? "Update" : "Add"}
          </StyledButton>
        </Grid>
        <Grid item xs={11} md={11} sm={11}>
          {error && <Typography style={{ color: "red" }}>{error}</Typography>}
        </Grid>
        <Grid item xs={11} sm={11}>
          {medicalHistoryData["prev"].length > 0 && (
            <>
              <StyledTableContainer
                component={Paper}
              // sx={{
              //   marginTop: "20px",
              //   border: "1px solid #21cdc0",
              // }}
              >
                <Table>
                  <TableHead>
                    <StyledTableRowHistory>
                      {fieldLayout.tableColumns.map((column) => (
                        <StyledTableCellHistory key={column.key}>
                          {column.label}
                        </StyledTableCellHistory>
                      ))}
                      <StyledTableCellHistory>
                        {showPrescriptionInclusion
                          ? "Include in Prescription"
                          : "Action"}
                      </StyledTableCellHistory>
                    </StyledTableRowHistory>
                  </TableHead>
                  <TableBody>
                    {medicalHistoryData["prev"].map((row, rowIndex) => (
                      <StyledTableRowHistory key={rowIndex}>
                        {fieldLayout.tableColumns.map((column) => (
                          <StyledTableCellHistory
                            key={column.key}
                            sx={column.key === "clinical_note" ? { width: "35%" } : undefined}
                          >
                            {renderMedicalTableCell(column, row, { isPrev: true })}
                          </StyledTableCellHistory>
                        ))}
                        <StyledTableCellHistory>
                          {showPrescriptionInclusion ? (
                            <StyledCheckbox
                              checked={!!row.include_in_prescription}
                              onChange={() =>
                                handlePrescriptionInclusionToggle(rowIndex)
                              }
                            />
                          ) : (
                            "-"
                          )}
                        </StyledTableCellHistory>
                      </StyledTableRowHistory>
                    ))}
                  </TableBody>
                </Table>
              </StyledTableContainer>
              {showPrescriptionInclusion && (
                <FormControlLabel
                  sx={{ mt: 1, ml: 0 }}
                  control={
                    <StyledCheckbox
                      checked={allPreviousSelectedForPrescription}
                      onChange={(e) =>
                        handleSelectAllPreviousPrescription(e.target.checked)
                      }
                    />
                  }
                  label="Select all from previous data"
                />
              )}
            </>
          )}
        </Grid>
        <Grid item xs={11} sm={11}>
          {medicalHistoryData["new"].length > 0 && (
            <StyledTableContainer
              component={Paper}
            // sx={{
            //   margin: "5px 2px",
            //   border: "1px solid #21cdc0",
            // }}
            >
              <Table>
                <TableHead>
                  <StyledTableRowHistory>
                    {fieldLayout.tableColumns.map((column) => (
                      <StyledTableCellHistory key={column.key}>
                        {column.label}
                      </StyledTableCellHistory>
                    ))}
                    <StyledTableCellHistory>Action</StyledTableCellHistory>
                  </StyledTableRowHistory>
                </TableHead>
                <TableBody>
                  {medicalHistoryData["new"].map((row, rowIndex) => (
                    <StyledTableRowHistory key={rowIndex}>
                      {fieldLayout.tableColumns.map((column) => (
                        <StyledTableCellHistory
                          key={column.key}
                          sx={column.key === "clinical_note" ? { width: "35%" } : undefined}
                        >
                          {renderMedicalTableCell(column, row, { isNew: true })}
                        </StyledTableCellHistory>
                      ))}
                      <StyledTableCellHistory>
                        {medicalHistoryData["prev"].includes(row) ? (
                          "-"
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                            }}
                          >
                            <Tooltip title="Edit">
                              <OutlinedIconFab
                                onClick={() =>
                                  handleMedicalHistoryEditClick(row, rowIndex)
                                }
                                sx={{ marginRight: 1 }}
                              >
                                <Icon name="editInTable" size={32} />
                              </OutlinedIconFab>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <OutlinedIconFab
                                onClick={() =>
                                  handleMedicalHistoryDelete(rowIndex)
                                }
                              >
                                <Icon name="deleteInTable" size={32} />
                              </OutlinedIconFab>
                            </Tooltip>
                          </div>
                        )}
                      </StyledTableCellHistory>
                    </StyledTableRowHistory>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
        </Grid>
      </Grid>
    </Card>
  );
};

export default React.memo(MedicalHistory);
